import { useState, type ReactNode } from "react";
import { CircleSlash, MoreHorizontal, PauseCircle } from "lucide-react";

import type { EstimateDetailViewItem } from "@shared-core/data/api-types";
import {
  estimateControlsPresentation,
  type EstimateControlState,
  type EstimateWriteIntent,
} from "@shared-core/domain/estimate-controls";

import { Button } from "../../primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../primitives/dropdown-menu";
import { cn } from "../../primitives/utils";
import { ParkChaseDialog } from "./ParkChaseDialog";
import { RecordOutcomeDialog } from "./RecordOutcomeDialog";

/**
 * The estimate's ⋯ menu: the two status controls (Record outcome, Park /
 * chase), after any other occasional actions the page passes in.
 *
 * A control the estimate withholds stays in the menu with its reason printed
 * under it — withheld-with-a-reason rather than hidden, because a CSR looking
 * for a control on a locked estimate needs to be told it is locked; a missing
 * item reads as a broken page. A hover-only tooltip is not "says why", so the
 * reason is visible text, and the item is `aria-disabled` rather than
 * `disabled`: Radix skips a disabled item on the keyboard, which would hide
 * the reason from anyone not using a mouse. A caution on an available
 * control is printed the same way.
 *
 * Both controls write for real (POST .../setstatus) — nothing here is a mock.
 */

type Props = {
  estimate: EstimateDetailViewItem;
  /** Other occasional actions, as menu items, listed first. */
  extraItems?: ReactNode;
  /**
   * Notified on every confirmed intent. Optional: the page does not need it,
   * but stories and any future wiring do.
   */
  onIntent?: (intent: EstimateWriteIntent) => void;
  /**
   * Called once Record outcome or Park / chase has actually written a new
   * status, so the page can re-read the estimate and show the consequence —
   * the status pill and the controls' own availability both depend on it.
   */
  onEstimateChanged?: () => void;
};

/**
 * One status control as a menu item, its reason or caution printed under its
 * label. A withheld item stays focusable and selecting it does nothing (the
 * menu stays open, so the reason stays readable).
 */
function ControlItem({
  label,
  icon: Icon,
  state,
  onSelect,
}: {
  label: string;
  icon: typeof CircleSlash;
  state: EstimateControlState;
  onSelect: () => void;
}) {
  const note = state.available
    ? state.caution
    : (state.blockedReason ?? "Not available on this estimate.");
  return (
    <DropdownMenuItem
      aria-disabled={state.available ? undefined : true}
      className={cn("items-start", !state.available && "text-muted-foreground")}
      onSelect={(event) => {
        if (!state.available) {
          event.preventDefault();
          return;
        }
        onSelect();
      }}
    >
      <Icon className="mt-0.5 w-4 h-4" />
      <span className="flex max-w-xs flex-col">
        <span>{label}</span>
        {note && (
          <span className="whitespace-normal text-xs text-muted-foreground">
            {note}
          </span>
        )}
      </span>
    </DropdownMenuItem>
  );
}

export function EstimateControls({
  estimate,
  extraItems,
  onIntent,
  onEstimateChanged,
}: Props) {
  const { recordOutcome, parkChase } = estimateControlsPresentation(estimate);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [parkOpen, setParkOpen] = useState(false);

  // The write already landed by the time this fires (OutcomeConfirmStep only
  // calls back on success), so there is nothing to accumulate — just notify
  // and let the page re-read the estimate.
  function handleWritten(intent: EstimateWriteIntent) {
    onIntent?.(intent);
    onEstimateChanged?.();
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" aria-label="More actions">
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-52">
          {extraItems}
          <ControlItem
            label="Record outcome"
            icon={CircleSlash}
            state={recordOutcome}
            onSelect={() => setOutcomeOpen(true)}
          />
          <ControlItem
            label="Park / chase"
            icon={PauseCircle}
            state={parkChase}
            onSelect={() => setParkOpen(true)}
          />
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Mounted unconditionally, open-state driven — availability gates
          only the ITEMS above. Gating the mounts too would mean a successful
          write (which re-fetches the estimate and can flip a control's
          availability) could unmount a dialog mid-exit-animation, and
          unmounting an animating Radix dialog risks stranding its portal in
          document.body. A closed dialog renders nothing anyway. */}
      <RecordOutcomeDialog
        estimate={estimate}
        open={outcomeOpen && recordOutcome.available}
        onOpenChange={setOutcomeOpen}
        onRecorded={handleWritten}
      />
      <ParkChaseDialog
        estimate={estimate}
        open={parkOpen && parkChase.available}
        onOpenChange={setParkOpen}
        onParked={handleWritten}
      />
    </>
  );
}
